import { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { ImageIcon, Pencil, Trash2, FolderPlus, ChevronDown, Trash } from 'lucide-react';
import Breadcrumb from '../../components/UI/Breadcrumb/Breadcrumb';
import LoadingSpinner from '../../components/UI/LoadingSpinner/LoadingSpinner';
import OptimizedImage from '../../components/OptimizedImage/OptimizedImage';
import { useAuth } from '../../contexts/AuthContext';
import { useImageGroups } from '../../hooks/useImageGroups';
import { getImageList } from '../../services/video';
import apiService from '../../services/api';
import './MyImages.css';

const PAGE_SIZE = 12;
const USE_MOCK  = false;
const ALL_TAB   = 'all';
const SIZE_LABELS = { '1k': '1080P', '2k': '2K', '4k': '4K' };

/* ── Mock data ── */
const MOCK_ITEMS = [
  { task_id: 'm01', status: 4, created_at: '2026-02-20 10:12', url: 'https://picsum.photos/seed/img01/400/600', task_param: { title: '婚纱写真', prompt: '让图1的女孩穿上图2的白色婚纱，面部特征与参考图片保持一致，清晨花园光线', size: '2k', aspectRatio: '9:16' } },
  { task_id: 'm02', status: 4, created_at: '2026-02-20 11:30', url: 'https://picsum.photos/seed/img02/600/400', task_param: { title: '商业广告图', prompt: '现代简约风格的产品展示，白色背景，柔和光线，专业摄影感', size: '2k', aspectRatio: '16:9' } },
  { task_id: 'm03', status: 4, created_at: '2026-02-20 14:05', url: 'https://picsum.photos/seed/img03/500/500', task_param: { title: '头像替换', prompt: '将人物头像迁移到西装背景，保持面部特征不变，专业商务风格', size: '1k', aspectRatio: '1:1' } },
  { task_id: 'm04', status: 2, created_at: '2026-02-21 09:00', url: '', task_param: { title: '古风人像', prompt: '唐朝宫廷风格，红色宫装，金色发饰，古典宫廷背景，工笔画风格', size: '4k', aspectRatio: '3:4' } },
  { task_id: 'm05', status: 1, created_at: '2026-02-21 09:15', url: '', task_param: { title: '科幻场景', prompt: '2077年赛博朋克城市夜景，霓虹灯光，雨天，未来感建筑，电影级画质', size: '4k', aspectRatio: '21:9' } },
  { task_id: 'm06', status: 3, created_at: '2026-02-21 08:30', url: '', task_param: { title: '风景合成', prompt: '将人物放置于挪威峡湾背景中，自然融合光线，写实摄影风格', size: '2k', aspectRatio: '16:9' } },
  { task_id: 'm07', status: 4, created_at: '2026-02-21 10:20', url: 'https://picsum.photos/seed/img07/400/533', task_param: { title: '时装大片', prompt: '高端时尚杂志风格，模特身着设计师礼服，工作室打光，背景虚化', size: '2k', aspectRatio: '3:4' } },
  { task_id: 'm08', status: 4, created_at: '2026-02-21 11:45', url: 'https://picsum.photos/seed/img08/600/338', task_param: { title: '产品主图', prompt: '电商主图风格，护肤品放置于大理石台面，自然光线，简洁干净的背景', size: '1k', aspectRatio: '16:9' } },
  { task_id: 'm09', status: 4, created_at: '2026-02-22 09:30', url: 'https://picsum.photos/seed/img09/500/500', task_param: { title: '儿童写真', prompt: '阳光草地，儿童奔跑嬉戏，温暖色调，胶片感，快门定格瞬间', size: '2k', aspectRatio: '1:1' } },
  { task_id: 'm10', status: 4, created_at: '2026-02-22 10:15', url: 'https://picsum.photos/seed/img10/400/600', task_param: { title: '水墨风格迁移', prompt: '将参考照片转换为中国传统水墨画风格，保留人物轮廓，墨色渲染', size: '2k', aspectRatio: '2:3' } },
  { task_id: 'm11', status: 4, created_at: '2026-02-22 13:00', url: 'https://picsum.photos/seed/img11/600/400', task_param: { title: '旅游纪念照', prompt: '将人物合成到巴黎埃菲尔铁塔前，黄金时刻光线，自然真实的合成效果', size: '2k', aspectRatio: '4:3' } },
  { task_id: 'm12', status: 1, created_at: '2026-02-22 14:30', url: '', task_param: { title: '食物摄影', prompt: '精致日料摆盘，俯拍角度，禅意风格，深色木质背景，专业美食摄影', size: '1k', aspectRatio: '1:1' } },
  { task_id: 'm13', status: 4, created_at: '2026-02-23 09:00', url: 'https://picsum.photos/seed/img13/400/600', task_param: { title: '概念艺术', prompt: '奇幻森林，发光蘑菇，精灵元素，梦幻紫色调，数字艺术风格', size: '4k', aspectRatio: '9:16' } },
  { task_id: 'm14', status: 4, created_at: '2026-02-23 10:30', url: 'https://picsum.photos/seed/img14/533/400', task_param: { title: '建筑效果图', prompt: '现代极简主义别墅，清晨薄雾，水面倒影，建筑可视化渲染', size: '4k', aspectRatio: '4:3' } },
  { task_id: 'm15', status: 2, created_at: '2026-02-23 11:00', url: '', task_param: { title: '国潮海报', prompt: '中国风潮流海报设计，龙纹元素，金红配色，现代街头艺术风格', size: '2k', aspectRatio: '9:16' } },
  { task_id: 'm16', status: 4, created_at: '2026-02-23 14:20', url: 'https://picsum.photos/seed/img16/500/500', task_param: { title: '证件照优化', prompt: '将参考照片处理为标准证件照规格，蓝色背景，面部光线均匀', size: '1k', aspectRatio: '1:1' } },
  { task_id: 'm17', status: 4, created_at: '2026-02-24 09:10', url: 'https://picsum.photos/seed/img17/600/400', task_param: { title: '电影海报', prompt: '科幻电影海报风格，主角站于星际背景前，戏剧性光影，英文字体排版', size: '4k', aspectRatio: '16:9' } },
  { task_id: 'm18', status: 3, created_at: '2026-02-24 10:00', url: '', task_param: { title: '换装测试', prompt: '将女孩图1替换为图2的汉服，面部特征和表情保持不变', size: '2k', aspectRatio: '3:4' } },
  { task_id: 'm19', status: 4, created_at: '2026-02-24 11:30', url: 'https://picsum.photos/seed/img19/400/600', task_param: { title: '油画风格', prompt: '将人物肖像转换为17世纪荷兰黄金时代油画风格，暗色背景，伦勃朗光线', size: '2k', aspectRatio: '2:3' } },
  { task_id: 'm20', status: 4, created_at: '2026-02-25 09:00', url: 'https://picsum.photos/seed/img20/600/600', task_param: { title: '宠物艺术照', prompt: '金毛猎犬在阳光草地上奔跑，浅景深，暖色调，宠物摄影大师风格', size: '2k', aspectRatio: '1:1' } },
];

const buildMockResponse = (page, pageSize) => {
  const start = (page - 1) * pageSize;
  return {
    code: 0,
    data: {
      total: MOCK_ITEMS.length,
      page,
      pageSize,
      items: MOCK_ITEMS.slice(start, start + pageSize),
    },
  };
};

/* ── Status pill ── */
const getStatusPill = (status) => {
  switch (status) {
    case 4: return { label: '已完成', cls: 'mimg-status-pill--success' };
    case 3: return { label: '失败',   cls: 'mimg-status-pill--failed' };
    case 2: return { label: '生成中', cls: 'mimg-status-pill--processing' };
    case 1: return { label: '排队中', cls: 'mimg-status-pill--queued' };
    default: return { label: '未知',  cls: 'mimg-status-pill--queued' };
  }
};

const MyImages = () => {
  const { user } = useAuth();
  const { groups, assignments, createGroup, renameGroup, deleteGroup, assignImages } =
    useImageGroups(user?.phone);

  // ── API state ──
  const [loading, setLoading]                     = useState(false);
  const [apiError, setApiError]                   = useState('');
  const [imageListResponse, setImageListResponse] = useState(null);
  const [currentPage, setCurrentPage]             = useState(1);
  const [retryCount, setRetryCount]               = useState(0);

  // ── UI state ──
  const [searchQuery, setSearchQuery]     = useState('');
  const [activeGroupTab, setActiveGroupTab] = useState(ALL_TAB);
  const [selectedImages, setSelectedImages] = useState([]);
  const [showMoveMenu, setShowMoveMenu]   = useState(false);

  // Group rename state
  const [renamingGroupId, setRenamingGroupId] = useState(null);
  const [renameGroupValue, setRenameGroupValue] = useState('');
  const renameGroupInputRef = useRef(null);

  // New group input state
  const [showNewGroupInput, setShowNewGroupInput] = useState(false);
  const [newGroupName, setNewGroupName]           = useState('');
  const newGroupInputRef = useRef(null);

  // Image inline rename state
  const [renamingId, setRenamingId]   = useState(null);
  const [renameValue, setRenameValue] = useState('');
  const renameInputRef = useRef(null);

  // ── Fetch ──
  const refetchImages = useCallback(() => {
    setRetryCount((prev) => prev + 1);
  }, []);

  useEffect(() => {
    let cancelled = false;
    const fetchData = async () => {
      try {
        setLoading(true);
        setApiError('');
        const data = USE_MOCK
          ? buildMockResponse(currentPage, PAGE_SIZE)
          : await getImageList({
              page: currentPage,
              pageSize: PAGE_SIZE,
              search: searchQuery,
              groupId: activeGroupTab === ALL_TAB ? '' : activeGroupTab,
            });
        if (!cancelled) {
          if (data.code !== undefined && data.code !== 0)
            throw new Error(data.msg || `Error ${data.code}`);
          setImageListResponse(data);
        }
      } catch (err) {
        if (!cancelled) { setApiError(err.message || '加载失败'); setImageListResponse(null); }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    fetchData();
    return () => { cancelled = true; };
  }, [currentPage, retryCount, searchQuery, activeGroupTab]);

  // ── Map items ──
  const images = useMemo(() => {
    const items = imageListResponse?.data?.items;
    if (!Array.isArray(items)) return [];
    return items.map((item, idx) => ({
      id:          item.task_id || `img-${idx}`,
      title:       item.task_param?.title || `图片 ${idx + 1}`,
      prompt:      item.task_param?.prompt || '',
      size:        item.task_param?.size || '',
      aspectRatio: item.task_param?.aspectRatio || '',
      createdAt:   item.created_at || '',
      imageUrl:    item.url || '',
      status:      item.status,
    }));
  }, [imageListResponse]);

  // ── Filter by group tab ──
  // Backend now handles group filtering, so we don't need client-side filtering.
  const tabImages = useMemo(() => {
    return images;
  }, [images]);

  // ── Get group name for badge display on ALL_TAB ──
  const getGroupName = useCallback((imageId) => {
    const gid = assignments[String(imageId)];
    if (!gid) return null;
    return groups.find((g) => g.id === gid)?.name || null;
  }, [assignments, groups]);

  // ── Filter by search ──
  const filtered = useMemo(() => {
    if (!searchQuery) return tabImages;
    const q = searchQuery.toLowerCase();
    return tabImages.filter(
      (img) => img.title.toLowerCase().includes(q) || img.prompt.toLowerCase().includes(q)
    );
  }, [tabImages, searchQuery]);

  const totalItems = imageListResponse?.data?.total || images.length;
  const totalPages = Math.max(1, Math.ceil(totalItems / PAGE_SIZE));

  // ── Page clamp ──
  useEffect(() => {
    if (totalPages > 0 && currentPage > totalPages) setCurrentPage(totalPages);
  }, [totalPages, currentPage]);

  // ── Reset page / selection when tab or search changes ──
  useEffect(() => { setCurrentPage(1); setSelectedImages([]); }, [activeGroupTab, searchQuery]);

  // ── Focus helpers ──
  useEffect(() => { if (showNewGroupInput) newGroupInputRef.current?.focus(); }, [showNewGroupInput]);
  useEffect(() => { if (renamingGroupId)   renameGroupInputRef.current?.focus(); }, [renamingGroupId]);
  useEffect(() => { if (renamingId)        renameInputRef.current?.focus(); }, [renamingId]);

  // ── Group operations ──
  const handleCreateGroup = useCallback(async () => {
    const name = newGroupName.trim();
    if (!name) return;
    if (groups.some((g) => g.name === name)) {
      alert(`分组名称「${name}」已存在，请使用其他名称`);
      return;
    }
    try {
      const id = await createGroup(name);
      setActiveGroupTab(id);
      setNewGroupName('');
      setShowNewGroupInput(false);
    } catch (error) {
      alert(`创建分组失败: ${error.message || '请重试'}`);
    }
  }, [newGroupName, createGroup, groups]);

  const handleStartRenameGroup = useCallback(() => {
    const g = groups.find((g) => g.id === activeGroupTab);
    if (!g) return;
    setRenamingGroupId(g.id);
    setRenameGroupValue(g.name);
  }, [groups, activeGroupTab]);

  const handleConfirmRenameGroup = useCallback(() => {
    const name = renameGroupValue.trim();
    if (!name || !renamingGroupId) { setRenamingGroupId(null); return; }
    if (groups.some((g) => g.id !== renamingGroupId && g.name === name)) {
      alert(`分组名称「${name}」已存在，请使用其他名称`);
      return;
    }
    renameGroup(renamingGroupId, name);
    setRenamingGroupId(null);
  }, [renameGroupValue, renamingGroupId, renameGroup, groups]);

  const handleDeleteActiveGroup = useCallback(() => {
    const g = groups.find((g) => g.id === activeGroupTab);
    if (!g) return;
    if (!window.confirm(`确认删除分组「${g.name}」？\n该分组内的图片将移出此标签，仍可在"全部"中查看。`)) return;
    deleteGroup(g.id);
    setActiveGroupTab(ALL_TAB);
  }, [groups, activeGroupTab, deleteGroup]);

  // ── Selection & batch ops ──
  const toggleSelect = useCallback((id) => {
    setSelectedImages((prev) =>
      prev.includes(id) ? prev.filter((v) => v !== id) : [...prev, id]
    );
  }, []);

  const handleMoveToGroup = useCallback(async (groupId) => {
    try {
      await assignImages(selectedImages, groupId || null);
      setSelectedImages([]);
      setShowMoveMenu(false);
    } catch (error) {
      alert(`分配失败: ${error.message || '请重试'}`);
    }
  }, [selectedImages, assignImages]);

  const handleBatchDelete = useCallback(async () => {
    if (selectedImages.length === 0) return;
    if (!window.confirm(`确认删除选中的 ${selectedImages.length} 张图片？删除后无法恢复。`)) return;
    try {
      await apiService.batchDeleteImages(selectedImages);
      // 刷新列表以反映删除结果
      await refetchImages();
      setSelectedImages([]);
      alert(`成功删除 ${selectedImages.length} 张图片`);
    } catch (err) {
      alert(err.message || '批量删除失败，请重试');
    }
  }, [selectedImages, refetchImages]);

  // ── Image inline rename ──
  const handleStartRename = (img, e) => {
    e.stopPropagation();
    setRenamingId(img.id);
    setRenameValue(img.title);
  };

  const handleConfirmRename = useCallback(async () => {
    const name = renameValue.trim();
    if (!name || !renamingId) { setRenamingId(null); return; }
    try {
      await apiService.renameImage(renamingId, name);
      setImageListResponse((prev) => {
        if (!prev) return prev;
        return {
          ...prev,
          data: {
            ...prev.data,
            items: (prev.data?.items || []).map((item) =>
              item.task_id === renamingId
                ? { ...item, task_param: { ...item.task_param, title: name } }
                : item
            ),
          },
        };
      });
      setRenamingId(null);
    } catch (err) {
      alert(err.message || '重命名失败，请重试');
    }
  }, [renameValue, renamingId]);

  // ── Single delete ──
  const handleDelete = async (img, e) => {
    e.stopPropagation();
    if (!window.confirm(`确认删除图片「${img.title}」？删除后无法恢复。`)) return;
    try {
      await apiService.deleteImage(img.id);
      // 刷新列表以反映删除结果
      await refetchImages();
    } catch (err) {
      alert(err.message || '删除失败，请重试');
    }
  };

  const handlePageChange = (page) => {
    if (page < 1 || page > totalPages) return;
    setCurrentPage(page);
  };

  const breadcrumbItems = [
    { label: '首页', path: '/dashboard' },
    { label: '我的图片', path: null },
  ];

  const moveTargets = groups.filter((g) => g.id !== activeGroupTab);

  return (
    <div className="mimg-page">
      <Breadcrumb items={breadcrumbItems} />
      <h1 className="mimg-page-title">我的图片</h1>

      {/* ── Tab Bar ── */}
      <div className="mimg-tab-bar">
        <button
          className={`mimg-tab${activeGroupTab === ALL_TAB ? ' active' : ''}`}
          onClick={() => setActiveGroupTab(ALL_TAB)}
        >
          全部
        </button>

        {groups.map((g) => (
          <button
            key={g.id}
            className={`mimg-tab${activeGroupTab === g.id ? ' active' : ''}`}
            onClick={() => setActiveGroupTab(g.id)}
          >
            <span className="mimg-tab-name">{g.name}</span>
          </button>
        ))}

        {showNewGroupInput ? (
          <div className="mimg-new-group-input-wrap">
            <input
              ref={newGroupInputRef}
              className="mimg-new-group-input"
              placeholder="分组名称"
              value={newGroupName}
              maxLength={20}
              onChange={(e) => setNewGroupName(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') handleCreateGroup();
                if (e.key === 'Escape') { setShowNewGroupInput(false); setNewGroupName(''); }
              }}
            />
            <button className="mimg-new-group-confirm" onClick={handleCreateGroup}>确认</button>
            <button
              className="mimg-new-group-cancel"
              onClick={() => { setShowNewGroupInput(false); setNewGroupName(''); }}
            >取消</button>
          </div>
        ) : (
          <button className="mimg-new-group-btn" onClick={() => setShowNewGroupInput(true)}>
            <FolderPlus size={14} />新建分组
          </button>
        )}
      </div>

      {/* ── Toolbar ── */}
      <div className="mimg-toolbar">
        <input
          className="mimg-search-box"
          placeholder="搜索图片标题或描述..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
        />
        <span className="mimg-total-count">共 {filtered.length} 张图片</span>

        {activeGroupTab !== ALL_TAB && (
          renamingGroupId ? (
            <div className="mimg-group-rename-wrap">
              <input
                ref={renameGroupInputRef}
                className="mimg-group-rename-input"
                value={renameGroupValue}
                onChange={(e) => setRenameGroupValue(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') handleConfirmRenameGroup();
                  if (e.key === 'Escape') setRenamingGroupId(null);
                }}
              />
              <button className="mimg-group-rename-ok" onClick={handleConfirmRenameGroup}>确认</button>
              <button className="mimg-group-rename-cancel" onClick={() => setRenamingGroupId(null)}>取消</button>
            </div>
          ) : (
            <div className="mimg-group-actions">
              <button className="mimg-group-rename-btn" onClick={handleStartRenameGroup}>
                <Pencil size={13} />重命名
              </button>
              <button className="mimg-group-del-btn" onClick={handleDeleteActiveGroup}>
                <Trash2 size={13} />删除分组
              </button>
            </div>
          )
        )}
      </div>

      {/* ── Batch Action Bar ── */}
      {selectedImages.length > 0 && (
        <div className="mimg-batch-bar">
          <span className="mimg-batch-info">已选 {selectedImages.length} 张图片</span>

          <div className="mimg-move-wrap">
            {showMoveMenu && (
              <div className="mimg-move-backdrop" onClick={() => setShowMoveMenu(false)} />
            )}
            <button className="mimg-move-btn" onClick={() => setShowMoveMenu((p) => !p)}>
              移至分组 <ChevronDown size={13} />
            </button>
            {showMoveMenu && (
              <div className="mimg-move-menu">
                {groups.length === 0 ? (
                  <div className="mimg-move-item" style={{ color: '#9ca3af', cursor: 'default' }}>
                    暂无分组，请先新建
                  </div>
                ) : (
                  moveTargets.map((g) => (
                    <button key={g.id} className="mimg-move-item" onClick={() => handleMoveToGroup(g.id)}>
                      {g.name}
                    </button>
                  ))
                )}
                {activeGroupTab !== ALL_TAB && (
                  <button className="mimg-move-item ungrouped" onClick={() => handleMoveToGroup(null)}>
                    移出分组
                  </button>
                )}
              </div>
            )}
          </div>

          <button className="mimg-batch-del-btn" onClick={handleBatchDelete}>
            <Trash size={13} />批量删除
          </button>
          <button className="mimg-cancel-btn" onClick={() => setSelectedImages([])}>取消选择</button>
        </div>
      )}

      {/* ── Content ── */}
      {loading ? (
        <div className="mimg-loading"><LoadingSpinner /></div>
      ) : apiError ? (
        <div className="mimg-error">
          <span>加载失败：{apiError}</span>
          <button className="mimg-retry-btn" onClick={() => setRetryCount((c) => c + 1)}>重试</button>
        </div>
      ) : filtered.length === 0 ? (
        <div className="mimg-empty">
          <ImageIcon size={48} className="mimg-empty-icon" />
          <p className="mimg-empty-text">
            {searchQuery
              ? '没有找到匹配的图片'
              : activeGroupTab === ALL_TAB
                ? '还没有图片，去生成你的第一张图片吧'
                : '该标签下暂无图片'}
          </p>
        </div>
      ) : (
        <>
          <div className="mimg-cards-grid">
            {filtered.map((img) => {
              const pill        = getStatusPill(img.status);
              const isCompleted = img.status === 4;
              const isSelected  = selectedImages.includes(img.id);
              const sizeLabel   = SIZE_LABELS[img.size] || img.size?.toUpperCase() || '';

              return (
                <div
                  key={img.id}
                  className={`mimg-image-card${isSelected ? ' selected' : ''}`}
                >
                  {/* Thumbnail — click to toggle selection */}
                  <div
                    className="mimg-thumb"
                    style={{ cursor: 'pointer' }}
                    onClick={() => toggleSelect(img.id)}
                  >
                    {isCompleted && img.imageUrl ? (
                      <OptimizedImage
                        src={img.imageUrl}
                        alt={img.title}
                        className="mimg-thumb-media"
                      />
                    ) : (
                      <div className="mimg-thumb-placeholder">
                        <ImageIcon size={36} />
                      </div>
                    )}

                    {/* Status pill — top-left */}
                    <span className={`mimg-status-pill ${pill.cls}`}>{pill.label}</span>

                    {/* Group badge — bottom-left, only on ALL_TAB */}
                    {activeGroupTab === ALL_TAB && getGroupName(img.id) && (
                      <span className="mimg-group-badge">{getGroupName(img.id)}</span>
                    )}

                    {/* Checkbox — top-right */}
                    <div className="mimg-check-overlay" onClick={(e) => e.stopPropagation()}>
                      <input
                        type="checkbox"
                        className="mimg-check-input"
                        checked={isSelected}
                        onChange={() => toggleSelect(img.id)}
                      />
                    </div>
                  </div>

                  {/* Body */}
                  <div className="mimg-body">
                    {renamingId === img.id ? (
                      <>
                        <input
                          ref={renameInputRef}
                          className="mimg-title-input"
                          value={renameValue}
                          maxLength={100}
                          onChange={(e) => setRenameValue(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') handleConfirmRename();
                            if (e.key === 'Escape') setRenamingId(null);
                          }}
                          onClick={(e) => e.stopPropagation()}
                        />
                        <div className="mimg-title-input-actions">
                          <button className="mimg-title-confirm" onClick={handleConfirmRename}>确认</button>
                          <button className="mimg-title-cancel" onClick={() => setRenamingId(null)}>取消</button>
                        </div>
                      </>
                    ) : (
                      <p className="mimg-title" title={img.title}>{img.title}</p>
                    )}

                    {img.prompt && (
                      <span className="mimg-prompt" title={img.prompt}>{img.prompt}</span>
                    )}

                    {(sizeLabel || img.aspectRatio) && (
                      <div className="mimg-tags">
                        {sizeLabel       && <span className="mimg-tag">{sizeLabel}</span>}
                        {img.aspectRatio && <span className="mimg-tag">{img.aspectRatio}</span>}
                      </div>
                    )}

                    {img.createdAt && <span className="mimg-meta">{img.createdAt}</span>}
                  </div>

                  {/* Actions */}
                  <div className="mimg-actions">
                    {isCompleted && img.imageUrl && (
                      <>
                        <button
                          className="mimg-view-btn"
                          onClick={() => window.open(img.imageUrl, '_blank')}
                        >
                          查看
                        </button>
                        <a
                          className="mimg-dl-btn"
                          href={img.imageUrl}
                          download
                          target="_blank"
                          rel="noreferrer"
                        >
                          下载
                        </a>
                      </>
                    )}
                    <button
                      className="mimg-rename-btn"
                      onClick={(e) => handleStartRename(img, e)}
                      disabled={renamingId === img.id}
                    >
                      重命名
                    </button>
                    <button className="mimg-del-btn" onClick={(e) => handleDelete(img, e)}>
                      删除
                    </button>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="mimg-pagination">
              <button
                className="mimg-page-btn"
                disabled={currentPage === 1}
                onClick={() => handlePageChange(currentPage - 1)}
              >←</button>

              {Array.from({ length: Math.min(totalPages, 7) }, (_, i) => {
                let n;
                if (totalPages <= 7)                    n = i + 1;
                else if (currentPage <= 4)              n = i + 1;
                else if (currentPage >= totalPages - 3) n = totalPages - 6 + i;
                else                                    n = currentPage - 3 + i;
                return (
                  <button
                    key={n}
                    className={`mimg-page-num${currentPage === n ? ' active' : ''}`}
                    onClick={() => handlePageChange(n)}
                  >{n}</button>
                );
              })}

              <button
                className="mimg-page-btn"
                disabled={currentPage === totalPages}
                onClick={() => handlePageChange(currentPage + 1)}
              >→</button>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default MyImages;
